import React, { useState } from 'react';
import { GraduationCap, Calendar, Award, Users, Mail, ArrowRight } from 'lucide-react';

const Academy = () => {
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [firstName, setFirstName] = useState('');
  const [state, setState] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clean and normalize the email input
    const normalizedEmail = email.trim().toLowerCase();
    
    // Basic structure validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
      setError('Please enter a valid email address');
      return;
    }
  
    // Length validation
    if (normalizedEmail.length > 254) {
      setError('Email address is too long');
      return;
    }
  
    const [localPart, domain] = normalizedEmail.split('@');
    if (localPart.length > 64) {
      setError('Email username is too long');
      return;
    }
  
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${apiUrl}/academy-waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: normalizedEmail,
          whatsapp,
          firstName,
          state 
        })
      });
  
      const data = await response.json();
  
      if (response.ok) {
        setIsSubmitted(true);
        setError('');
      } else {
        setError(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    }
  };

  return (
    <section className="py-12 sm:py-16 bg-gradient-to-b from-rose-50 to-white relative overflow-hidden px-4 sm:px-6 lg:px-8">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute w-48 h-48 sm:w-72 sm:h-72 rounded-full bg-rose-300 blur-2xl sm:blur-3xl -top-12 sm:-top-20 -left-12 sm:-left-20" />
        <div className="absolute w-48 h-48 sm:w-72 sm:h-72 rounded-full bg-pink-300 blur-2xl sm:blur-3xl -bottom-12 sm:-bottom-20 -right-12 sm:-right-20" />
      </div>

      <div className="max-w-6xl mx-auto relative">
        {/* Coming Soon Badge */}
        <div className="flex justify-center mb-6 sm:mb-8">
          <span className="bg-rose-100 text-rose-600 text-sm font-semibold px-4 py-1 rounded-full animate-pulse">
            Coming Soon
          </span>
        </div>

        {/* Academy Icon */}
        <div className="flex justify-center mb-4">
          <GraduationCap className="w-8 h-8 sm:w-10 sm:h-10 text-red-600" />
        </div>

        {/* Heading and Description */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">
            Piel Perfecta Academy
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Transform your passion for wellness into a rewarding career. Join our premier massage therapy training program.
          </p>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12">
          <div className="bg-white p-5 sm:p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="bg-rose-100 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mb-3 sm:mb-4">
              <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-rose-600" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold mb-2">Flexible Schedule</h3>
            <p className="text-sm sm:text-base text-gray-600">
              Part-time and full-time programs designed to fit your lifestyle
            </p>
          </div>

          <div className="bg-white p-5 sm:p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="bg-rose-100 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mb-3 sm:mb-4">
              <Award className="w-5 h-5 sm:w-6 sm:h-6 text-rose-600" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold mb-2">Certified Training</h3>
            <p className="text-sm sm:text-base text-gray-600">
              Industry-recognized certification and hands-on practical experience
            </p>
          </div>

          <div className="bg-white p-5 sm:p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="bg-rose-100 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mb-3 sm:mb-4">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-rose-600" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold mb-2">Expert Mentorship</h3>
            <p className="text-sm sm:text-base text-gray-600">
              Learn from experienced professionals in a real spa environment
            </p>
          </div>
        </div>

        {/* Pre-registration Form */}
        <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-xl max-w-2xl mx-auto">
          {!isSubmitted ? (
            <>
              <h3 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 text-center">
                Be First to Know
              </h3>
              <p className="text-sm sm:text-base text-gray-600 text-center mb-4 sm:mb-6">
                Join our priority waitlist to receive exclusive early-bird offers and program updates.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* FirstName Input */}
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    setError('');
                  }}
                  placeholder="Enter your first name"
                  className="w-full px-4 py-2 sm:py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-rose-400"
                  required
                />
                {/* State Input */}
                <input
                  type="text"
                  value={state}
                  onChange={(e) => {
                    setState(e.target.value);
                    setError('');
                  }}
                  placeholder="Current state of residence"
                  className="w-full px-4 py-2 sm:py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-rose-400"
                  required
                />
                {/* Email Input */}
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  placeholder="Enter your email"
                  className="w-full px-4 py-2 sm:py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-rose-400"
                  required
                />
                {/* Whatsapp Number Input */}
                <input
                  type="tel"
                  value={whatsapp}
                  onChange={(e) => {
                    setWhatsapp(e.target.value);
                    setError('');
                  }}
                  placeholder="Enter your WhatsApp number"
                  className="w-full px-4 py-2 sm:py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-rose-400"
                  required
                />
                {/* Form Error display */}
                {error && (
                  <p className="text-red-500 text-sm text-center">{error}</p>
                )}
                <button 
                  type="submit"
                  className="w-full bg-rose-500 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-rose-600 transition-colors group"
                >
                  Join Waitlist
                  <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
              <p className="text-xs sm:text-sm text-gray-500 mt-3 sm:mt-4 text-center">
                Be one of the first 50 to register and receive a special discount on tuition.
              </p>
            </>
          ) : (
            <div className="text-center">
              <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 text-rose-600">
                Thank You for Joining!
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                You've been added to our priority waitlist. We'll send exclusive updates to {email}.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Academy;